import { LinkButton } from '@understory-io/leaf/link-button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage } from 'react-intl';
import { useLocaleHref } from '~/lib/use-locale-href';

type CrossSellCardProps = {
  /** Which cross-sell variant to render. Drives icon, copy, and target. */
  kind: 'giftcard' | 'punchcard';
};

/**
 * Promotional tile linking to the gift-card and punch-card pages. Rendered by
 * `CrossSellSection` inside an "Other options" row — both variants share the
 * same visual shape so the row reads as a pair.
 */
export function CrossSellCard({ kind }: CrossSellCardProps) {
  const localeHref = useLocaleHref();

  if (kind === 'giftcard') {
    return (
      <article className={cardStyles}>
        <Stack gap="medium">
          <Text as="h3" style="subheadline-large">
            <FormattedMessage
              defaultMessage="Gift card"
              description="Heading on the gift card cross-sell tile on the home page"
            />
          </Text>
          <Text as="p" style="body-large-regular" color="secondary">
            <FormattedMessage
              defaultMessage="Give an experience as a gift — flexible to redeem on any of our experiences."
              description="Body copy on the gift card cross-sell tile on the home page"
            />
          </Text>
        </Stack>
        <span className={ctaWrapStyles}>
          <LinkButton
            href={localeHref('/:locale?/giftcards')}
            appearance="secondary"
            size="large"
          >
            <FormattedMessage
              defaultMessage="Buy a gift card"
              description="CTA on the gift card cross-sell tile on the home page"
            />
          </LinkButton>
        </span>
      </article>
    );
  }

  return (
    <article className={cardStyles}>
      <Stack gap="medium">
        <Text as="h3" style="subheadline-large">
          <FormattedMessage
            defaultMessage="Punch card"
            description="Heading on the punch card cross-sell tile on the home page"
          />
        </Text>
        <Text as="p" style="body-large-regular" color="secondary">
          <FormattedMessage
            defaultMessage="Going more than once? Save with a punch card and book whenever it suits you."
            description="Body copy on the punch card cross-sell tile on the home page"
          />
        </Text>
      </Stack>
      <span className={ctaWrapStyles}>
        <LinkButton
          href={localeHref('/:locale?/punchcards')}
          appearance="secondary"
          size="large"
        >
          <FormattedMessage
            defaultMessage="Buy a punch card"
            description="CTA on the punch card cross-sell tile on the home page"
          />
        </LinkButton>
      </span>
    </article>
  );
}

const cardStyles = css({
  flex: '1',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 'large',
  background: 'surface.raised',
  borderRadius: 'large',
  border: 'small',
  borderColor: 'border.neutral',
  padding: 'large',
});

const ctaWrapStyles = css({
  alignSelf: 'flex-start',
});
